import {
  STATBLOCK_DE,
  formatMod,
  naturalCompare,
  statblockOf,
  summarizeStatblock,
  type NoteWithProps,
} from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Combobox } from '../components/Combobox';
import { MarkdownBody } from '../components/MarkdownView';
import { NoteLink } from '../components/NoteLink';
import { useInvalidateCampaign, useNote } from '../hooks';
import { t, tDyn } from '../i18n';

const ROLL_KINDS = ['check', 'save', 'attack', 'damage', 'initiative', 'other'] as const;

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function sessionSort(a: NoteWithProps, b: NoteWithProps): number {
  const na = Number(a.frontmatter.number);
  const nb = Number(b.frontmatter.number);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return nb - na;
  const da = String(a.frontmatter.date ?? '');
  const db = String(b.frontmatter.date ?? '');
  if (da !== db) return db.localeCompare(da);
  return naturalCompare(b.title, a.title);
}

/** Abschnitt „## Log“ aus dem Text schneiden. */
function logSection(body: string): string {
  const lines = body.split('\n');
  const start = lines.findIndex((l) => /^##\s+Log\s*$/i.test(l));
  if (start === -1) return '';
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^#{1,2}\s/.test(lines[i]!)) {
      end = i;
      break;
    }
  }
  return lines.slice(start + 1, end).join('\n').trim();
}

function loadCurrent(cid: string): string | null {
  try {
    return localStorage.getItem(`kt.session.${cid}`);
  } catch {
    return null;
  }
}

export function SessionPage() {
  const { cid = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const invalidate = useInvalidateCampaign(cid);
  const sessions = useQuery({ queryKey: ['notesOfType', cid, 'session'], queryFn: () => api.notesOfType(cid, 'session') });
  const party = useQuery({ queryKey: ['notesOfType', cid, 'charakter'], queryFn: () => api.notesOfType(cid, 'charakter') });
  const quests = useQuery({ queryKey: ['notesOfType', cid, 'quest'], queryFn: () => api.notesOfType(cid, 'quest') });
  const combats = useQuery({ queryKey: ['combats', cid], queryFn: () => api.combats(cid) });
  const [currentId, setCurrentId] = useState<string | null>(() => loadCurrent(cid));
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => [...(sessions.data ?? [])].sort(sessionSort), [sessions.data]);
  const current = sorted.find((s) => s.id === currentId) ?? sorted[0];
  const note = useNote(cid, current?.id);
  const pcs = useMemo(() => [...(party.data ?? [])].sort((a, b) => naturalCompare(a.title, b.title)), [party.data]);

  useEffect(() => {
    document.title = `${t('session.title')} · Kartothek`;
  }, []);

  const choose = (id: string) => {
    setCurrentId(id);
    try {
      localStorage.setItem(`kt.session.${cid}`, id);
    } catch {
      /* egal */
    }
  };

  const createSession = async () => {
    setError(null);
    try {
      const max = Math.max(0, ...(sessions.data ?? []).map((s) => Number(s.frontmatter.number) || 0));
      const n = max + 1;
      const created = await api.createNote(cid, {
        title: `Session ${n}`,
        folder: 'Sessions',
        typeKey: 'session',
        frontmatter: { number: n, date: todayISO(), characters: pcs.map((p) => `[[${p.title}]]`) },
        body: '## Zusammenfassung\n\n## Log\n\n',
      });
      invalidate();
      await qc.invalidateQueries({ queryKey: ['notesOfType', cid, 'session'] });
      choose(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const log = async (text: string) => {
    if (!current) return;
    const res = await api.appendLog(cid, current.id, text, nowHHMM());
    qc.setQueryData(['note', cid, current.id], res);
  };

  const activeQuests = (quests.data ?? []).filter((q) => q.frontmatter.status === 'aktiv');
  const openCombats = (combats.data ?? []).filter((k) => k.status !== 'ended');

  return (
    <div className="dashboard">
      <header className="dashboard-head">
        <h1>{t('session.title')}</h1>
        <select value={current?.id ?? ''} onChange={(e) => choose(e.target.value)} aria-label={t('session.current')}>
          {sorted.length === 0 && <option value="">{t('session.none')}</option>}
          {sorted.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
              {s.frontmatter.date ? ` · ${String(s.frontmatter.date)}` : ''}
            </option>
          ))}
        </select>
        <button onClick={() => void createSession()}>+ {t('session.new')}</button>
        {current && (
          <NoteLink cid={cid} noteId={current.id} preview={false}>
            {t('session.openNote')}
          </NoteLink>
        )}
      </header>
      {error && <div className="error banner">{error}</div>}

      <div className="dashboard-grid">
        <div className="dash-main">
          {current ? (
            <>
              <QuickLog onLog={log} />
              <DiceEntry pcs={pcs} onLog={log} />
              <section className="dash-card">
                <h2>{t('session.log')}</h2>
                {note.data ? (
                  logSection(note.data.body) ? (
                    <MarkdownBody
                      cid={cid}
                      body={logSection(note.data.body)}
                      links={note.data.links}
                      folder={note.data.folder}
                      depth={1}
                      chain={[note.data.id]}
                      isGameMaster
                      preview={false}
                    />
                  ) : (
                    <p className="muted">{t('session.logEmpty')}</p>
                  )
                ) : (
                  <p className="muted">{t('common.loading')}</p>
                )}
              </section>
            </>
          ) : (
            <p className="muted">{t('session.createFirst')}</p>
          )}
        </div>

        <aside className="dash-side">
          <section className="dash-card">
            <h2>{t('session.party')}</h2>
            {pcs.length === 0 && <p className="muted small">{t('session.noParty')}</p>}
            <table className="party-table">
              <thead>
                <tr>
                  <th />
                  <th title={STATBLOCK_DE.labels.ac}>{STATBLOCK_DE.labels.acShort}</th>
                  <th title={STATBLOCK_DE.labels.hp}>{STATBLOCK_DE.labels.hpShort}</th>
                  <th title={STATBLOCK_DE.labels.passivePerception}>{t('session.pp')}</th>
                  <th title={STATBLOCK_DE.labels.initiative}>{t('session.init')}</th>
                </tr>
              </thead>
              <tbody>
                {pcs.map((p) => {
                  const sb = statblockOf(p.frontmatter);
                  const s = sb ? summarizeStatblock(sb) : null;
                  return (
                    <tr key={p.id}>
                      <td>
                        <NoteLink cid={cid} noteId={p.id}>
                          {p.title}
                        </NoteLink>
                        {p.frontmatter.player ? <div className="muted small">{String(p.frontmatter.player)}</div> : null}
                      </td>
                      <td>{s?.ac ?? '–'}</td>
                      <td>{s?.hp ?? '–'}</td>
                      <td>{s?.passivePerception ?? '–'}</td>
                      <td>{s ? formatMod(s.initiative) : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <section className="dash-card">
            <h2>{t('combat.title')}</h2>
            {openCombats.map((k) => (
              <div key={k.id}>
                <Link to={`/c/${cid}/combat/${k.id}`}>{k.name}</Link>{' '}
                <span className="muted small">
                  {tDyn(`combat.status.${k.status}`)} · {t('combat.roundN', { n: k.round })}
                </span>
              </div>
            ))}
            <button
              disabled={!current}
              onClick={async () => {
                const k = await api.createCombat(cid, { name: t('combat.defaultName'), sessionNoteId: current?.id ?? null });
                void qc.invalidateQueries({ queryKey: ['combats', cid] });
                navigate(`/c/${cid}/combat/${k.id}`);
              }}
            >
              + {t('combat.new')}
            </button>
          </section>

          <section className="dash-card">
            <h2>
              {t('session.activeQuests')} <span className="count">{activeQuests.length}</span>
            </h2>
            {activeQuests.length === 0 && <p className="muted small">{t('session.noQuests')}</p>}
            <ul className="plain-list">
              {activeQuests.map((q) => (
                <li key={q.id}>
                  <NoteLink cid={cid} noteId={q.id}>
                    {q.title}
                  </NoteLink>
                </li>
              ))}
            </ul>
            <Link to={`/c/${cid}/quests`} className="small">
              {t('quests.title')} →
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}

function QuickLog({ onLog }: { onLog: (text: string) => Promise<void> }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="dash-card quick-log"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
          await onLog(text.trim());
          setText('');
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>{t('session.quickLog')}</h2>
      <div className="row">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('session.quickLogHint')} autoFocus />
        <button className="primary" disabled={busy || !text.trim()}>
          {t('session.add')}
        </button>
      </div>
    </form>
  );
}

/** Echte Würfelergebnisse schnell festhalten: Wer, was, Ergebnis → Log. */
function DiceEntry({ pcs, onLog }: { pcs: NoteWithProps[]; onLog: (text: string) => Promise<void> }) {
  const [who, setWho] = useState('');
  const [kind, setKind] = useState<(typeof ROLL_KINDS)[number]>('check');
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const valueRef = useRef<HTMLInputElement>(null);
  const skills = Object.values(STATBLOCK_DE.skills);
  const pcOptions = useMemo(() => pcs.map((p) => ({ key: p.id, title: p.title })), [pcs]);
  const abilities = Object.values(STATBLOCK_DE.abilityNames);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    const pc = pcs.find((p) => p.title === who);
    const name = pc ? `[[${pc.title}]]` : who.trim() || t('session.someone');
    const what = [tDyn(`session.roll.${kind}`), label.trim()].filter(Boolean).join(' – ');
    await onLog(`🎲 ${name}: ${what} **${value.trim()}**`);
    setValue('');
    valueRef.current?.focus();
  };

  return (
    <form className="dash-card dice-entry" onSubmit={submit}>
      <h2>{t('session.dice')}</h2>
      <div className="row wrap">
        <Combobox
          className="dice-who"
          value={who}
          onChange={setWho}
          options={pcOptions}
          onPick={(o) => {
            setWho(o.title);
            valueRef.current?.focus();
          }}
          placeholder={t('session.who')}
        />
        <select value={kind} onChange={(e) => setKind(e.target.value as (typeof ROLL_KINDS)[number])}>
          {ROLL_KINDS.map((k) => (
            <option key={k} value={k}>
              {tDyn(`session.roll.${k}`)}
            </option>
          ))}
        </select>
        <input list="dice-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('session.rollLabel')} />
        <datalist id="dice-label">
          {(kind === 'save' ? abilities : skills).map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <input
          ref={valueRef}
          className="dice-value"
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t('session.result')}
        />
        <button className="primary" disabled={!value.trim()}>
          {t('session.add')}
        </button>
      </div>
      <div className="quick-pcs">
        {pcs.map((p) => (
          <button
            type="button"
            key={p.id}
            className={who === p.title ? 'chip on' : 'chip'}
            onClick={() => {
              setWho(p.title);
              valueRef.current?.focus();
            }}
          >
            {p.title}
          </button>
        ))}
      </div>
    </form>
  );
}
